import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useWebSocket } from "../context/WebSocketContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "../components/ui/dialog";
import { Plus, Trash2, ExternalLink, Music2, Flame, Image as ImageIcon, Upload, Layers, Download, Star, StarOff, Loader2, Pencil, Save } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import useSubscription from "../hooks/useSubscription";
import { ProLock } from "../components/ProGate";

function tiktokVideoIdFromUrl(url) {
  const m = url.match(/\/video\/(\d+)/);
  return m ? m[1] : null;
}

function TikTokEmbed({ url }) {
  const id = tiktokVideoIdFromUrl(url);
  if (!id) return <a href={url} target="_blank" rel="noreferrer" className="text-blue-500 underline text-sm">Open on TikTok</a>;
  return (
    <iframe
      src={`https://www.tiktok.com/embed/v2/${id}`}
      className="w-full"
      style={{ aspectRatio: "9 / 16", border: "none" }}
      allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
      title={`TikTok ${id}`}
    />
  );
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function TransformationGallery({ isAdmin }) {
  const { subscribe } = useWebSocket();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ image_data_url: "", caption: "", kind: "progress", weight_kg: "", sort_order: 0 });
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    const { data } = await api.get("/coach/transformations");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  // Auto-refresh the gallery on every device the moment admin adds/deletes a photo.
  useEffect(() => {
    const unsub = subscribe("coach:transformations_updated", () => { load(); });
    return () => { unsub && unsub(); };
  }, [subscribe]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) { toast.error("Image too large (max 4 MB)"); return; }
    const dataUrl = await fileToDataUrl(f);
    setForm((p) => ({ ...p, image_data_url: dataUrl }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.image_data_url) { toast.error("Pick an image first"); return; }
    setUploading(true);
    try {
      await api.post("/coach/transformations", {
        ...form,
        weight_kg: form.weight_kg ? Number(form.weight_kg) : null,
        sort_order: Number(form.sort_order) || 0,
      });
      toast.success("Photo added");
      setOpen(false);
      setForm({ image_data_url: "", caption: "", kind: "progress", weight_kg: "", sort_order: 0 });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const removePhoto = async (tid) => {
    if (!window.confirm("Remove this photo?")) return;
    await api.delete(`/coach/transformations/${tid}`);
    load();
  };

  return (
    <section data-testid="transformation-gallery" className="mb-7">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-2xl uppercase">The Journey</h2>
        {isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-transformation-btn" className="bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-9">
                <Upload className="w-3 h-3 mr-1" /> Upload
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-neutral-950 border-neutral-800 text-white">
              <DialogHeader><DialogTitle className="font-display text-2xl uppercase">Add Transformation Photo</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-3">
                <div>
                  <Label className="text-xs tracking-widest uppercase text-neutral-400">Photo (max 4 MB)</Label>
                  <input data-testid="tr-file-input" type="file" accept="image/*" onChange={onFile} className="mt-2 block w-full text-sm text-neutral-300 file:mr-3 file:py-2 file:px-3 file:border-0 file:bg-blue-500 file:text-white file:uppercase file:tracking-wider file:font-display file:text-xs file:cursor-pointer" />
                  {form.image_data_url && <img src={form.image_data_url} alt="" className="mt-3 max-h-40 mx-auto" />}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs tracking-widest uppercase text-neutral-400">Kind</Label>
                    <select data-testid="tr-kind-select" value={form.kind} onChange={(e) => setForm({...form, kind: e.target.value})} className="mt-2 w-full bg-neutral-900 border border-neutral-800 text-white rounded-none h-11 px-3">
                      <option value="before">Before</option>
                      <option value="progress">Progress</option>
                      <option value="after">After</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs tracking-widest uppercase text-neutral-400">Weight (kg)</Label>
                    <Input data-testid="tr-weight-input" type="number" min={0} step={0.5} value={form.weight_kg} onChange={(e) => setForm({...form, weight_kg: e.target.value})} className="mt-2 bg-neutral-900 border-neutral-800 text-white rounded-none h-11" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs tracking-widest uppercase text-neutral-400">Caption</Label>
                  <Input data-testid="tr-caption-input" value={form.caption} onChange={(e) => setForm({...form, caption: e.target.value})} placeholder="e.g. 18 months in" className="mt-2 bg-neutral-900 border-neutral-800 text-white rounded-none h-11" />
                </div>
                <div>
                  <Label className="text-xs tracking-widest uppercase text-neutral-400">Sort order (lower = earlier)</Label>
                  <Input data-testid="tr-order-input" type="number" value={form.sort_order} onChange={(e) => setForm({...form, sort_order: e.target.value})} className="mt-2 bg-neutral-900 border-neutral-800 text-white rounded-none h-11" />
                </div>
                <Button data-testid="tr-submit-btn" type="submit" disabled={uploading} className="w-full bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-10">
                  {uploading ? "Uploading…" : "Add photo"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-neutral-800 p-8 text-center">
          <ImageIcon className="w-8 h-8 text-neutral-600 mx-auto mb-3" />
          <div className="text-sm text-neutral-400">{isAdmin ? "Upload your before/after photos to share the journey." : "Photos coming soon."}</div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {items.map((t) => (
            <div key={t.transformation_id} data-testid={`tr-${t.transformation_id}`} className="border border-neutral-800 relative group">
              <img src={t.image_data_url} alt={t.caption} className="w-full aspect-[3/4] object-cover" />
              <div className="absolute top-2 left-2 px-2 py-0.5 text-[10px] tracking-widest uppercase font-bold bg-black/80 text-blue-500 border border-blue-500/40">{t.kind}</div>
              {t.weight_kg != null && <div className="absolute top-2 right-2 px-2 py-0.5 text-[10px] tracking-widest uppercase font-bold bg-blue-500 text-white">{t.weight_kg}kg</div>}
              {t.caption && <div className="p-2 border-t border-neutral-800 text-xs text-neutral-300">{t.caption}</div>}
              {isAdmin && (
                <Button size="icon" variant="ghost" onClick={() => removePhoto(t.transformation_id)} data-testid={`tr-del-${t.transformation_id}`} className="absolute bottom-2 right-2 bg-black/80 text-neutral-400 hover:text-blue-500 h-7 w-7">
                  <Trash2 className="w-3 h-3" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function FeaturedSupersets({ isAdmin }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importingToken, setImportingToken] = useState(null);
  const [adminOpen, setAdminOpen] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [candLoading, setCandLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/coach/supersets");
      setItems(data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const oneTapImport = async (it) => {
    setImportingToken(it.share_token);
    try {
      const { data: saved } = await api.post(`/supersets/by-token/${it.share_token}/import`);
      toast.success(`"${saved.name}" added to your library`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not import");
    } finally {
      setImportingToken(null);
    }
  };

  const openAdmin = async () => {
    setAdminOpen(true);
    setCandLoading(true);
    try {
      const { data } = await api.get("/admin/coach/supersets/candidates");
      setCandidates(data || []);
    } catch (e) {
      toast.error("Could not load supersets");
    } finally {
      setCandLoading(false);
    }
  };

  const toggleFeature = async (ss) => {
    try {
      if (ss.is_featured) {
        await api.post(`/admin/coach/supersets/${ss.superset_id}/unfeature`);
        toast.success("Removed from gallery");
      } else {
        await api.post(`/admin/coach/supersets/${ss.superset_id}/feature`, null, { params: { sort_order: 0 } });
        toast.success("Pinned to gallery");
      }
      // refresh candidates and gallery
      const { data } = await api.get("/admin/coach/supersets/candidates");
      setCandidates(data || []);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <section data-testid="featured-supersets" className="mb-7">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-2xl uppercase">Grizz's Supersets</h2>
        {isAdmin && (
          <Dialog open={adminOpen} onOpenChange={(o) => { o ? openAdmin() : setAdminOpen(false); }}>
            <DialogTrigger asChild>
              <Button data-testid="manage-featured-ss-btn" className="bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-9">
                <Star className="w-3 h-3 mr-1" /> Manage
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-neutral-950 border-neutral-800 text-white max-w-2xl">
              <DialogHeader><DialogTitle className="font-display text-2xl uppercase">Curate Featured Supersets</DialogTitle></DialogHeader>
              <p className="text-xs text-neutral-400">Pin your saved supersets to the Coach Hub gallery. Pinned ones appear first; community top-imports fill the rest.</p>
              {candLoading ? (
                <div className="text-neutral-400 flex items-center gap-2 py-4"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
              ) : candidates.length === 0 ? (
                <div className="text-sm text-neutral-400 border border-dashed border-neutral-800 p-6 text-center">
                  No saved supersets yet. <Link to="/library" className="text-blue-500 underline">Build one</Link>.
                </div>
              ) : (
                <div className="max-h-[60vh] overflow-y-auto space-y-2">
                  {candidates.map((ss) => (
                    <div key={ss.superset_id} data-testid={`cand-${ss.superset_id}`} className="border border-neutral-800 p-3 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-display text-base uppercase truncate">{ss.name}</div>
                        <div className="text-[10px] text-neutral-500 uppercase tracking-widest">{(ss.exercises || []).length} exercises</div>
                      </div>
                      <Button
                        data-testid={`toggle-feature-${ss.superset_id}`}
                        onClick={() => toggleFeature(ss)}
                        className={`rounded-none uppercase tracking-wider font-display h-9 shrink-0 ${ss.is_featured ? "bg-neutral-800 hover:bg-neutral-700 text-blue-500" : "bg-blue-500 hover:bg-blue-600 text-white"}`}
                      >
                        {ss.is_featured ? (<><StarOff className="w-3 h-3 mr-1" /> Unpin</>) : (<><Star className="w-3 h-3 mr-1" /> Pin</>)}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </DialogContent>
          </Dialog>
        )}
      </div>

      {loading ? (
        <div className="text-neutral-400 flex items-center gap-2 py-4"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-neutral-800 p-8 text-center">
          <Layers className="w-8 h-8 text-neutral-600 mx-auto mb-3" />
          <div className="text-sm text-neutral-400">{isAdmin ? "Pin one of your supersets above to feature it here." : "Coach drops his curated supersets here. Stay tuned."}</div>
        </div>
      ) : (
        <div data-testid="featured-ss-carousel" className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 snap-x snap-mandatory">
          {items.map((it) => (
            <div
              key={it.superset_id}
              data-testid={`featured-ss-${it.superset_id}`}
              className="snap-start shrink-0 w-[78%] sm:w-[48%] border border-neutral-800 bg-gradient-to-br from-blue-500/5 to-transparent p-4 flex flex-col"
            >
              <div className="flex items-center gap-2 mb-2">
                {it.is_featured && (
                  <span className="px-1.5 py-0.5 text-[9px] tracking-widest uppercase font-bold bg-blue-500 text-white inline-flex items-center gap-1">
                    <Star className="w-2.5 h-2.5 fill-current" /> Coach Pick
                  </span>
                )}
                {!it.is_featured && it.import_count > 0 && (
                  <span className="px-1.5 py-0.5 text-[9px] tracking-widest uppercase font-bold bg-neutral-800 text-blue-500 border border-blue-500/40">
                    🔥 {it.import_count} imports
                  </span>
                )}
              </div>
              <div className="font-display text-xl uppercase leading-tight mb-1 truncate">{it.name}</div>
              <div className="text-[10px] text-neutral-500 uppercase tracking-widest mb-2">By {it.owner_name}</div>
              {it.description && <div className="text-xs text-neutral-400 mb-2 line-clamp-2">{it.description}</div>}
              <div className="text-xs text-neutral-300 mb-3 space-y-0.5">
                {(it.exercises || []).slice(0, 3).map((e, idx) => (
                  <div key={idx} className="flex items-center gap-1.5">
                    <span className="font-display text-blue-500 w-4">{String.fromCharCode(65 + idx)}</span>
                    <span className="truncate">{e.name}</span>
                  </div>
                ))}
                {(it.exercises || []).length > 3 && (
                  <div className="text-[10px] text-neutral-500 pl-5">+ {(it.exercises || []).length - 3} more</div>
                )}
              </div>
              <div className="mt-auto flex gap-2">
                <Button
                  data-testid={`import-ss-${it.superset_id}`}
                  onClick={() => oneTapImport(it)}
                  disabled={importingToken === it.share_token}
                  className="flex-1 bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-9 text-xs"
                >
                  {importingToken === it.share_token ? (
                    <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Adding…</>
                  ) : (
                    <><Download className="w-3 h-3 mr-1" /> One-tap add</>
                  )}
                </Button>
                <Link
                  to={`/supersets/import/${it.share_token}`}
                  data-testid={`preview-ss-${it.superset_id}`}
                  className="px-3 h-9 inline-flex items-center justify-center border border-neutral-700 text-neutral-300 hover:text-white hover:border-blue-500 text-xs uppercase tracking-wider font-display"
                >
                  Preview
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function FeaturedWorkouts({ isAdmin }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importingToken, setImportingToken] = useState(null);
  const [adminOpen, setAdminOpen] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [candLoading, setCandLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/marketplace/workouts");
      setItems(data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const oneTapImport = async (it) => {
    setImportingToken(it.share_token);
    try {
      const { data: saved } = await api.post(`/workouts/by-token/${it.share_token}/import`);
      const added = saved.new_exercises_added || 0;
      toast.success(added > 0
        ? `"${saved.name}" added — ${added} new exercise${added === 1 ? "" : "s"} dropped in`
        : `"${saved.name}" added to your library`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not import");
    } finally {
      setImportingToken(null);
    }
  };

  const openAdmin = async () => {
    setAdminOpen(true);
    setCandLoading(true);
    try {
      const { data } = await api.get("/admin/marketplace/workouts/candidates");
      setCandidates(data || []);
    } catch (e) {
      toast.error("Could not load workouts");
    } finally { setCandLoading(false); }
  };

  const toggleFeature = async (wk) => {
    try {
      if (wk.is_featured) {
        await api.post(`/admin/marketplace/workouts/${wk.workout_id}/unfeature`);
        toast.success("Removed from gallery");
      } else {
        await api.post(`/admin/marketplace/workouts/${wk.workout_id}/feature`, null, { params: { sort_order: 0 } });
        toast.success("Pinned to gallery");
      }
      const { data } = await api.get("/admin/marketplace/workouts/candidates");
      setCandidates(data || []);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <section data-testid="featured-workouts" className="mb-7">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-2xl uppercase">Grizz's Workouts</h2>
        {isAdmin && (
          <Dialog open={adminOpen} onOpenChange={(o) => { o ? openAdmin() : setAdminOpen(false); }}>
            <DialogTrigger asChild>
              <Button data-testid="manage-featured-wk-btn" className="bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-9">
                <Star className="w-3 h-3 mr-1" /> Manage
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-neutral-950 border-neutral-800 text-white max-w-2xl">
              <DialogHeader><DialogTitle className="font-display text-2xl uppercase">Curate Featured Workouts</DialogTitle></DialogHeader>
              <p className="text-xs text-neutral-400">Pin your saved workouts to the gallery. Pinned ones appear first; community top-imports fill the rest.</p>
              {candLoading ? (
                <div className="text-neutral-400 flex items-center gap-2 py-4"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
              ) : candidates.length === 0 ? (
                <div className="text-sm text-neutral-400 border border-dashed border-neutral-800 p-6 text-center">
                  No saved workouts yet. <Link to="/library" className="text-blue-500 underline">Build one</Link>.
                </div>
              ) : (
                <div className="max-h-[60vh] overflow-y-auto space-y-2">
                  {candidates.map((wk) => (
                    <div key={wk.workout_id} data-testid={`wk-cand-${wk.workout_id}`} className="border border-neutral-800 p-3 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-display text-base uppercase truncate">{wk.name}</div>
                        <div className="text-[10px] text-neutral-500 uppercase tracking-widest">{wk.category} · {(wk.exercises || []).length} exercises</div>
                      </div>
                      <Button
                        data-testid={`toggle-feature-wk-${wk.workout_id}`}
                        onClick={() => toggleFeature(wk)}
                        className={`rounded-none uppercase tracking-wider font-display h-9 shrink-0 ${wk.is_featured ? "bg-neutral-800 hover:bg-neutral-700 text-blue-500" : "bg-blue-500 hover:bg-blue-600 text-white"}`}
                      >
                        {wk.is_featured ? (<><StarOff className="w-3 h-3 mr-1" /> Unpin</>) : (<><Star className="w-3 h-3 mr-1" /> Pin</>)}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </DialogContent>
          </Dialog>
        )}
      </div>

      {loading ? (
        <div className="text-neutral-400 flex items-center gap-2 py-4"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-neutral-800 p-8 text-center">
          <Layers className="w-8 h-8 text-neutral-600 mx-auto mb-3" />
          <div className="text-sm text-neutral-400">{isAdmin ? "Pin a workout above to feature it here." : "Coach drops his curated workouts here. Stay tuned."}</div>
        </div>
      ) : (
        <div data-testid="featured-wk-carousel" className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 snap-x snap-mandatory">
          {items.map((it) => (
            <div
              key={it.workout_id}
              data-testid={`featured-wk-${it.workout_id}`}
              className="snap-start shrink-0 w-[78%] sm:w-[48%] border border-neutral-800 bg-gradient-to-br from-blue-500/5 to-transparent p-4 flex flex-col"
            >
              <div className="flex items-center gap-2 mb-2">
                {it.is_featured && (
                  <span className="px-1.5 py-0.5 text-[9px] tracking-widest uppercase font-bold bg-blue-500 text-white inline-flex items-center gap-1">
                    <Star className="w-2.5 h-2.5 fill-current" /> Coach Pick
                  </span>
                )}
                {!it.is_featured && it.import_count > 0 && (
                  <span className="px-1.5 py-0.5 text-[9px] tracking-widest uppercase font-bold bg-neutral-800 text-blue-500 border border-blue-500/40">
                    🔥 {it.import_count} imports
                  </span>
                )}
                <span className="ml-auto px-1.5 py-0.5 text-[9px] tracking-widest uppercase font-bold border border-neutral-700 text-neutral-400">
                  {it.category}
                </span>
              </div>
              <div className="font-display text-xl uppercase leading-tight mb-1 truncate">{it.name}</div>
              <div className="text-[10px] text-neutral-500 uppercase tracking-widest mb-2">By {it.owner_name}</div>
              {it.description && <div className="text-xs text-neutral-400 mb-2 line-clamp-2">{it.description}</div>}
              <div className="text-xs text-neutral-300 mb-3 space-y-0.5">
                {(it.exercises || []).slice(0, 3).map((e, idx) => (
                  <div key={idx} className="flex items-center gap-1.5">
                    <span className="font-display text-blue-500 w-4">{idx + 1}</span>
                    <span className="truncate">{e.name}</span>
                  </div>
                ))}
                {(it.exercises || []).length > 3 && (
                  <div className="text-[10px] text-neutral-500 pl-5">+ {(it.exercises || []).length - 3} more</div>
                )}
              </div>
              <div className="mt-auto flex gap-2">
                <Button
                  data-testid={`import-wk-${it.workout_id}`}
                  onClick={() => oneTapImport(it)}
                  disabled={importingToken === it.share_token}
                  className="flex-1 bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-9 text-xs"
                >
                  {importingToken === it.share_token ? (
                    <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Adding…</>
                  ) : (
                    <><Download className="w-3 h-3 mr-1" /> One-tap add</>
                  )}
                </Button>
                <Link
                  to={`/workouts/import/${it.share_token}`}
                  data-testid={`preview-wk-${it.workout_id}`}
                  className="px-3 h-9 inline-flex items-center justify-center border border-neutral-700 text-neutral-300 hover:text-white hover:border-blue-500 text-xs uppercase tracking-wider font-display"
                >
                  Preview
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}


function AboutTheCoach({ isAdmin, tiktokHandle, tiktokUrl }) {
  const { subscribe } = useWebSocket();
  const [about, setAbout] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/coach/about");
      setAbout(data);
    } catch {
      /* fall back to a minimal stub so the page still renders */
      setAbout({
        display_name: "Grizz",
        intro_paragraphs: [],
        outro_paragraph: "",
        quote: "",
        heaviest_weight_kg: null,
        current_weight_kg: null,
        lost_weight_kg: null,
      });
    }
  };
  useEffect(() => { load(); }, []);

  // Live propagation: any admin edit fans out `coach:about_updated` over WS
  // → every open Coach page (this one included) re-fetches, so the new bio /
  // weight appears on all devices instantly without anyone clearing cache.
  useEffect(() => {
    const unsub = subscribe("coach:about_updated", () => { load(); });
    return () => { unsub && unsub(); };
  }, [subscribe]);

  // Also re-fetch when the tab regains focus — covers PWAs on Android that
  // suspend WS sockets in the background.
  useEffect(() => {
    const onFocus = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("focus", onFocus);
    return () => {
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  const startEdit = () => {
    setForm({
      display_name: about?.display_name || "",
      from_location: about?.from_location || "",
      intro_paragraphs: (about?.intro_paragraphs || []).join("\n\n"),
      outro_paragraph: about?.outro_paragraph || "",
      quote: about?.quote || "",
      heaviest_weight_kg: about?.heaviest_weight_kg ?? "",
      current_weight_kg: about?.current_weight_kg ?? "",
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        display_name: form.display_name.trim() || null,
        from_location: form.from_location.trim() || null,
        intro_paragraphs: form.intro_paragraphs
          .split(/\n{2,}/)
          .map((p) => p.trim())
          .filter(Boolean),
        outro_paragraph: form.outro_paragraph.trim() || null,
        quote: form.quote.trim() || null,
        heaviest_weight_kg: form.heaviest_weight_kg !== "" ? Number(form.heaviest_weight_kg) : null,
        current_weight_kg: form.current_weight_kg !== "" ? Number(form.current_weight_kg) : null,
      };
      // Strip nulls so the backend exclude_unset path works
      const cleaned = Object.fromEntries(Object.entries(payload).filter(([_, v]) => v !== null));
      const { data } = await api.put("/admin/coach/about", cleaned);
      setAbout(data);
      toast.success("About section updated — live for everyone");
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  if (!about) {
    return (
      <section className="mb-7 border border-neutral-800 p-8 text-sm text-neutral-400 flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading bio…
      </section>
    );
  }

  const kgToLb = (kg) => (kg != null ? Math.round(Number(kg) * 2.2046) : null);
  const heaviestLb = kgToLb(about.heaviest_weight_kg);
  const currentLb = kgToLb(about.current_weight_kg);
  const lostLb = kgToLb(about.lost_weight_kg);

  return (
    <section data-testid="about-grizz" className="mb-7 border border-neutral-800">
      <div className="p-5 border-b border-neutral-800 bg-gradient-to-br from-blue-500/10 to-transparent flex items-start justify-between gap-3">
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-blue-500 font-bold mb-2">ABOUT THE COACH</div>
          <h2 className="font-display text-3xl uppercase leading-none">Meet {about.display_name || "Grizz"}</h2>
        </div>
        {isAdmin && (
          <Dialog open={open} onOpenChange={(o) => (o ? startEdit() : setOpen(false))}>
            <DialogTrigger asChild>
              <Button
                data-testid="edit-about-btn"
                size="sm"
                className="bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-9 shrink-0"
              >
                <Pencil className="w-3 h-3 mr-1.5" /> Edit
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-neutral-950 border-neutral-800 text-white max-w-xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl uppercase">Edit About the Coach</DialogTitle>
                <DialogDescription className="text-xs text-neutral-500 tracking-widest uppercase">
                  Saves go live for every user instantly — no refresh needed.
                </DialogDescription>
              </DialogHeader>
              {form && (
                <form onSubmit={save} className="space-y-3">
                  <div>
                    <Label className="text-xs tracking-widest uppercase text-neutral-400">Display name</Label>
                    <Input
                      data-testid="about-display-name-input"
                      value={form.display_name}
                      onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                      maxLength={80}
                      className="mt-2 bg-neutral-900 border-neutral-800 text-white rounded-none h-11"
                    />
                  </div>
                  <div>
                    <Label className="text-xs tracking-widest uppercase text-neutral-400">From</Label>
                    <Input
                      data-testid="about-from-input"
                      value={form.from_location}
                      onChange={(e) => setForm({ ...form, from_location: e.target.value })}
                      maxLength={120}
                      placeholder="Adelaide in Australia"
                      className="mt-2 bg-neutral-900 border-neutral-800 text-white rounded-none h-11"
                    />
                  </div>
                  <div>
                    <Label className="text-xs tracking-widest uppercase text-neutral-400">Bio paragraphs (separate with blank line)</Label>
                    <Textarea
                      data-testid="about-intro-input"
                      value={form.intro_paragraphs}
                      onChange={(e) => setForm({ ...form, intro_paragraphs: e.target.value })}
                      rows={7}
                      className="mt-2 bg-neutral-900 border-neutral-800 text-white rounded-none"
                    />
                    <div className="text-[10px] text-neutral-500 mt-1 tracking-widest uppercase">Tip: hit ENTER twice between paragraphs.</div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs tracking-widest uppercase text-neutral-400">Heaviest (kg)</Label>
                      <Input
                        data-testid="about-heaviest-input"
                        type="number"
                        min={0}
                        step={0.1}
                        value={form.heaviest_weight_kg}
                        onChange={(e) => setForm({ ...form, heaviest_weight_kg: e.target.value })}
                        className="mt-2 bg-neutral-900 border-neutral-800 text-white rounded-none h-11"
                      />
                    </div>
                    <div>
                      <Label className="text-xs tracking-widest uppercase text-neutral-400">Today (kg)</Label>
                      <Input
                        data-testid="about-current-input"
                        type="number"
                        min={0}
                        step={0.1}
                        value={form.current_weight_kg}
                        onChange={(e) => setForm({ ...form, current_weight_kg: e.target.value })}
                        className="mt-2 bg-neutral-900 border-neutral-800 text-white rounded-none h-11"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs tracking-widest uppercase text-neutral-400">Closing paragraph</Label>
                    <Textarea
                      data-testid="about-outro-input"
                      value={form.outro_paragraph}
                      onChange={(e) => setForm({ ...form, outro_paragraph: e.target.value })}
                      rows={3}
                      className="mt-2 bg-neutral-900 border-neutral-800 text-white rounded-none"
                    />
                  </div>
                  <div>
                    <Label className="text-xs tracking-widest uppercase text-neutral-400">Pull-quote</Label>
                    <Input
                      data-testid="about-quote-input"
                      value={form.quote}
                      onChange={(e) => setForm({ ...form, quote: e.target.value })}
                      maxLength={300}
                      className="mt-2 bg-neutral-900 border-neutral-800 text-white rounded-none h-11"
                    />
                  </div>
                  <Button
                    data-testid="about-save-btn"
                    type="submit"
                    disabled={saving}
                    className="w-full bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-10"
                  >
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save & go live
                  </Button>
                </form>
              )}
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="p-5 space-y-4">
        {(about.intro_paragraphs || []).map((p, idx) => (
          <p
            key={idx}
            data-testid={`about-paragraph-${idx}`}
            className={idx === 0 ? "text-neutral-200 leading-relaxed" : "text-neutral-300 leading-relaxed text-sm"}
          >
            {p}
          </p>
        ))}

        {/* Transformation stats — pulled from the same editable doc */}
        <div data-testid="grizz-stats" className="grid grid-cols-3 gap-2 my-2">
          <div className="border border-neutral-800 p-3 text-center">
            <div className="text-[10px] tracking-widest uppercase text-neutral-500">Heaviest</div>
            <div data-testid="stat-heaviest-kg" className="font-display text-2xl text-neutral-300 mt-1">
              {about.heaviest_weight_kg ?? "—"}<span className="text-xs text-neutral-500"> kg</span>
            </div>
            <div className="text-[10px] text-neutral-500 mt-0.5">{heaviestLb != null ? `${heaviestLb} lb` : ""}</div>
          </div>
          <div className="border-2 border-blue-500 p-3 text-center bg-blue-500/5">
            <div className="text-[10px] tracking-widest uppercase text-blue-500">Today</div>
            <div data-testid="stat-current-kg" className="font-display text-2xl text-white mt-1">
              {about.current_weight_kg ?? "—"}<span className="text-xs text-neutral-400"> kg</span>
            </div>
            <div className="text-[10px] text-neutral-400 mt-0.5">{currentLb != null ? `${currentLb} lb` : ""}</div>
          </div>
          <div className="border border-neutral-800 p-3 text-center">
            <div className="text-[10px] tracking-widest uppercase text-neutral-500">Lost</div>
            <div data-testid="stat-lost-kg" className="font-display text-2xl text-blue-500 mt-1">
              {about.lost_weight_kg ?? "—"}<span className="text-xs text-neutral-500"> kg</span>
            </div>
            <div className="text-[10px] text-neutral-500 mt-0.5">{lostLb != null ? `${lostLb} lb · so far` : ""}</div>
          </div>
        </div>

        {about.outro_paragraph && (
          <p className="text-neutral-200 leading-relaxed">{about.outro_paragraph}</p>
        )}
        {about.quote && (
          <blockquote className="border-l-2 border-blue-500 pl-4 py-1">
            <div data-testid="about-quote-text" className="font-display text-xl uppercase text-white leading-tight">"{about.quote}"</div>
            <div className="text-xs text-neutral-500 uppercase tracking-widest mt-1">— {about.display_name || "Grizz"}</div>
          </blockquote>
        )}

        <div className="grid grid-cols-3 gap-2 pt-2">
          {[
            { label: "Daily Drops", icon: Music2 },
            { label: "Real Form Fixes", icon: Flame },
            { label: "Weekly Challenges", icon: Music2 },
          ].map((b) => (
            <div key={b.label} className="border border-neutral-800 p-3 text-center">
              <b.icon className="w-4 h-4 text-blue-500 mx-auto mb-1" />
              <div className="text-[10px] tracking-widest uppercase text-neutral-300">{b.label}</div>
            </div>
          ))}
        </div>
        {tiktokUrl && (
          <a href={tiktokUrl} target="_blank" rel="noreferrer" data-testid="follow-grizz-btn">
            <Button className="w-full bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-10 mt-2">
              <ExternalLink className="w-4 h-4 mr-2" /> Follow @{tiktokHandle}
            </Button>
          </a>
        )}
      </div>
    </section>
  );
}


export default function Coach() {
  const { user } = useAuth();
  const { sub } = useSubscription();
  const [profile, setProfile] = useState(null);
  const [highlights, setHighlights] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ tiktok_url: "", title: "", description: "" });

  useEffect(() => {
    (async () => {
      const [p, h, a] = await Promise.all([
        api.get("/coach/profile"),
        api.get("/coach/highlights"),
        api.get("/me/is-admin"),
      ]);
      setProfile(p.data); setHighlights(h.data); setIsAdmin(a.data.is_admin);
    })();
  }, []);

  const addHighlight = async (e) => {
    e.preventDefault();
    if (!form.tiktok_url.includes("tiktok.com")) { toast.error("Paste a TikTok video URL"); return; }
    try {
      const { data } = await api.post("/coach/highlights", form);
      setHighlights((h) => [data, ...h]);
      setForm({ tiktok_url: "", title: "", description: "" });
      setOpen(false);
      toast.success("Highlight added");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const removeHighlight = async (id) => {
    if (!window.confirm("Remove this highlight?")) return;
    await api.delete(`/coach/highlights/${id}`);
    setHighlights((h) => h.filter((x) => x.highlight_id !== id));
  };

  if (!user) return null;

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6">
      <div className="text-xs tracking-[0.3em] uppercase text-blue-500 font-bold flex items-center gap-2">
        <Music2 className="w-3 h-3" /> COACH CHANNEL
      </div>
      <h1 className="font-display text-4xl sm:text-5xl uppercase leading-none mt-1 mb-1">Train with Grizz</h1>
      <p className="text-sm text-neutral-400 mb-5">Daily fitness drops, weekly challenges, real talk. Straight from <span className="text-blue-500">@{profile?.tiktok_handle}</span>.</p>

      {sub && !sub.is_pro && (
        <ProLock feature="Coach Hub access" testId="pro-lock-coach" className="mb-6" />
      )}

      <div className="border border-neutral-800 p-4 mb-6 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-blue-500 flex items-center justify-center"><Flame className="w-7 h-7 text-white" strokeWidth={2.5} /></div>
          <div>
            <div className="font-display text-xl uppercase">@{profile?.tiktok_handle}</div>
            <div className="text-xs text-neutral-500">Live drops · Challenges · Form fixes</div>
          </div>
        </div>
        <a data-testid="tiktok-profile-link" href={profile?.tiktok_url} target="_blank" rel="noreferrer">
          <Button variant="outline" className="bg-transparent border-neutral-700 text-white rounded-none uppercase tracking-wider font-display h-9">
            <ExternalLink className="w-3 h-3 mr-2" /> Open TikTok
          </Button>
        </a>
      </div>

      {/* About Grizz */}
      <AboutTheCoach
        isAdmin={isAdmin}
        tiktokHandle={profile?.tiktok_handle}
        tiktokUrl={profile?.tiktok_url}
      />

      {/* Transformation Gallery */}
      <TransformationGallery isAdmin={isAdmin} />

      {/* Featured Supersets gallery (one-tap import) */}
      <FeaturedSupersets isAdmin={isAdmin} />

      {/* Featured Workouts gallery (one-tap import) */}
      <FeaturedWorkouts isAdmin={isAdmin} />

      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-2xl uppercase">Featured Drops</h2>
        {isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-highlight-btn" className="bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-9">
                <Plus className="w-4 h-4 mr-1" /> Pin
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-neutral-950 border-neutral-800 text-white">
              <DialogHeader><DialogTitle className="font-display text-2xl uppercase">Pin a TikTok</DialogTitle></DialogHeader>
              <form onSubmit={addHighlight} className="space-y-3">
                <div>
                  <Label className="text-xs tracking-widest uppercase text-neutral-400">TikTok video URL</Label>
                  <Input data-testid="hl-url-input" value={form.tiktok_url} onChange={(e) => setForm({...form, tiktok_url: e.target.value})} placeholder="https://www.tiktok.com/@grizz.au/video/123..." className="mt-2 bg-neutral-900 border-neutral-800 text-white rounded-none" />
                </div>
                <div>
                  <Label className="text-xs tracking-widest uppercase text-neutral-400">Title</Label>
                  <Input data-testid="hl-title-input" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="mt-2 bg-neutral-900 border-neutral-800 text-white rounded-none" />
                </div>
                <div>
                  <Label className="text-xs tracking-widest uppercase text-neutral-400">Description</Label>
                  <Textarea data-testid="hl-desc-input" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="mt-2 bg-neutral-900 border-neutral-800 text-white rounded-none" />
                </div>
                <Button data-testid="hl-submit-btn" type="submit" className="w-full bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-10">Pin highlight</Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {highlights.length === 0 ? (
        <div className="border border-dashed border-neutral-800 p-8 text-center text-sm text-neutral-400">
          {isAdmin ? "Pin a TikTok above to feature it here." : "Featured drops coming soon."}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {highlights.map((h) => (
            <div key={h.highlight_id} data-testid={`highlight-${h.highlight_id}`} className="border border-neutral-800">
              <TikTokEmbed url={h.tiktok_url} />
              <div className="p-3 border-t border-neutral-800">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    {h.title && <div className="font-display text-lg uppercase leading-tight">{h.title}</div>}
                    {h.description && <div className="text-xs text-neutral-400 mt-1">{h.description}</div>}
                  </div>
                  {isAdmin && (
                    <Button size="icon" variant="ghost" onClick={() => removeHighlight(h.highlight_id)} data-testid={`hl-del-${h.highlight_id}`} className="text-neutral-400 hover:text-blue-500 h-7 w-7 shrink-0">
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
